export const answerFeedbackReasons = {
  WRONG_ANSWER: 'wrongAnswer',
  MISSING_DATA: 'missingData',
  MISUNDERSTOOD: 'misunderstood',
  SOMETHING_ELSE: 'somethingElse',
} as const;

export type AnswerFeedbackReason =
  typeof answerFeedbackReasons[keyof typeof answerFeedbackReasons];

// The panel's view of a rating; the server stores it as like or dislike.
export type AnswerFeedback = {
  rating: 'positive' | 'negative';
  reason?: AnswerFeedbackReason;
  comment?: string;
};

export type ChatAttachment = {
  id?: string;
  name: string;
  mediaType: string;
};

export type ChatMessage = {
  messageIndex: number;
  role: 'user' | 'assistant';
  content: string;
  attachments?: ChatAttachment[];
  feedback?: {
    rating: 'like' | 'dislike';
    comment?: string;
    reason?: AnswerFeedbackReason;
  };
  // Set on a reply that just arrived and cleared once it has been written out,
  // so reopening the panel does not retype what the member already watched.
  justReceived?: boolean;
  // The exchange ended without the server storing a visible answer. Carried on a
  // placed-in message rather than a real one, so it is never rated or persisted.
  emptyReply?: boolean;
  // A send that failed before the server answered. Never typed, rated or persisted.
  error?: boolean;
  // The question was stored but a later status check failed, so an answer may still come.
  connectionLost?: boolean;
};

export type CostBreakdown = {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  totalMainCostUsd: number;
  totalValidatorCostUsd: number;
};

export type ChatSessionDetail = CostBreakdown & {
  id: string;
  name: string;
  processing: boolean;
  cancelled?: boolean;
  isArchived: boolean;
  sessionType?: 'formstudio';
  sessionTypeArgs?: { formId: string };
  messages?: ChatMessage[];
};

export type ChatMessageFeedbackInput = {
  rating: 'like' | 'dislike';
  comment?: string;
  reason?: AnswerFeedbackReason;
};

// What POST /chat accepts, as opposed to the session record it returns.
export type ChatExchangeInput = {
  message: string;
  sessionId?: string;
  pageContext?: string;
  attachments?: { name: string; mediaType: string; data: string }[];
};

export type ChatExchange = CostBreakdown & {
  id: string;
  name: string;
  processing: boolean;
  isArchived: boolean;
  reply?: string;
};

export type ChatSessionListItem = {
  id: string;
  name: string;
  createdAt: string;
  processing: boolean;
  isArchived: boolean;
  totalCostUsd: number;
};

export type PanelSize = {
  width: number;
  height: number;
};
